import React from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Stack, Typography } from '@mui/material';
import Colors from '../../../../library/Colors';

/**
 * Lightweight wrapper used by every per-section config card. Centralises the
 * Paper styling, title+description header and inner spacing so each card file
 * only has to focus on its own fields. Pass `sx` to override per-card.
 *
 * `headerActions` renders inline on the right of the title — handy for
 * search boxes / "Add new" buttons that would otherwise eat a full row.
 */
const ConfigCard = ({ title, description, headerActions, theme = 'light', children, sx }) => {
    const isDark = theme === 'dark';
    const cardBg     = isDark ? '#2d333b' : '#ffffff';
    const borderCol  = isDark ? '#3a3f47' : '#e4e7eb';
    const textColor  = isDark ? Colors.dark_text : Colors.light_text;
    const mutedText  = isDark ? '#b0b8c1' : '#768390';

    return (
        <Paper elevation={0} sx={{
            p: 2.5,
            borderRadius: 2,
            border: `1px solid ${borderCol}`,
            backgroundColor: cardBg,
            ...sx,
        }}>
            <Box sx={{
                mb: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textColor, lineHeight: 1.2 }}>
                        {title}
                    </Typography>
                    {description && (
                        <Typography variant="caption" sx={{ color: mutedText, display: 'block', mt: 0.5 }}>
                            {description}
                        </Typography>
                    )}
                </Box>
                {headerActions && (
                    <Box sx={{ flexShrink: 0 }}>{headerActions}</Box>
                )}
            </Box>
            <Stack spacing={2}>
                {children}
            </Stack>
        </Paper>
    );
};

ConfigCard.propTypes = {
    title:         PropTypes.node.isRequired,
    description:   PropTypes.node,
    headerActions: PropTypes.node,
    theme:         PropTypes.string,
    children:      PropTypes.node,
    sx:            PropTypes.object,
};

export default ConfigCard;
